/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-lines-per-function */

import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {FilterInput} from 'plugins/modules/filter/components/FilterInput';
import style from './style.module.scss';
import {DEBOUNCE_DELAY_SECOND} from 'core/constants';
import {Radio} from 'plugins/modules/filter/components/Radio';
import {IFilterFacetEnum} from 'plugins/modules/filter/types';
import _debounce from 'lodash/debounce';
import {getLocaleNumber} from 'core/helpers';
import {isPriceFilterByUnit} from 'plugins/modules/filter/helpers';

interface IProps {
    defaultMinValue?: number | null;
    defaultMaxValue?: number | null;
    onApply: (values: [string, string]) => void;
    minRange?: number;
    maxRange?: number;
    unit?: string;
    options?: IFilterFacetEnum[];
    withCount?: boolean;
}

export const FilterRangeInputs: FC<IProps> = ({
    defaultMinValue,
    defaultMaxValue,
    onApply,
    minRange = 0,
    maxRange = 1000000,
    unit,
    options,
    withCount = true,
}) => {
    const [minValue, setMinValue] = useState<string>(null !== defaultMinValue ? String(defaultMinValue) : '');
    const [maxValue, setMaxValue] = useState<string>(null !== defaultMaxValue ? String(defaultMaxValue) : '');

    useEffect(() => {
        setMinValue(null !== defaultMinValue ? String(defaultMinValue) : '');
    }, [defaultMinValue]);
    useEffect(() => {
        setMaxValue(null !== defaultMaxValue ? String(defaultMaxValue) : '');
    }, [defaultMaxValue]);

    const normalizeValue = useCallback((value: string, min: number, max: number): string => {
        if ('' === value) {
            return '';
        }
        const num = Number(value);
        if (isNaN(num)) {
            return '';
        }
        if (num < min && 0 !== num) {
            return String(min);
        }
        if (num > max && 0 !== num) {
            return String(max);
        }
        return 0 !== num ? String(num) : '';
    }, []);

    const debouncedApply = useMemo(
        () =>
            _debounce((min: string, max: string) => {
                let normMin = normalizeValue(min, minRange, maxRange);
                let normMax = normalizeValue(max, minRange, maxRange);

                if (Number.parseFloat(normMin) > Number.parseFloat(normMax) && Boolean(normMin) && Boolean(normMax)) {
                    [normMin, normMax] = [normMax, normMin];
                }
                setMinValue(normMin);
                setMaxValue(normMax);
                onApply([normMin, normMax]);
            }, DEBOUNCE_DELAY_SECOND * 1.5),
        [normalizeValue, minRange, maxRange, onApply],
    );

    const handleChangeMin = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;

            if ('' !== value && !/^([0-9]+([.][0-9]*)?|[.][0-9]+)$/.test(value)) {
                return;
            }

            setMinValue(value);
            debouncedApply(value, maxValue);
        },
        [debouncedApply, maxValue],
    );

    const handleChangeMax = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;

            if ('' !== value && !/^([0-9]+([.][0-9]*)?|[.][0-9]+)$/.test(value)) {
                return;
            }

            setMaxValue(value);
            debouncedApply(minValue, value);
        },
        [debouncedApply, minValue],
    );

    const isPrice = useMemo(() => isPriceFilterByUnit(unit), [unit]);

    const maxPlaceholder = useMemo(() => {
        const unitString = unit ? ` ${unit}` : '';

        return `до ${isPrice ? getLocaleNumber(maxRange) : maxRange}${unitString}`;
    }, [isPrice, maxRange, unit]);

    const minPlaceholder = useMemo(() => {
        const unitString = unit ? ` ${unit}` : '';

        return `от ${isPrice ? getLocaleNumber(minRange) : minRange}${unitString}`;
    }, [isPrice, minRange, unit]);

    const handleSelectOption = useCallback(
        (min: number, max: number) => {
            // Отменяем debounce перед мгновенным применением
            debouncedApply.cancel();
            const minStr = null !== min && min !== undefined ? String(min) : '';
            const maxStr = null !== max && max !== undefined ? String(max) : '';
            const normMin = normalizeValue(minStr, minRange, maxRange);
            const normMax = normalizeValue(maxStr, minRange, maxRange);
            setMinValue(normMin);
            setMaxValue(normMax);
            onApply([normMin, normMax]);
        },
        [minRange, maxRange, onApply, normalizeValue, debouncedApply],
    );

    const isShowInputs = Boolean(maxRange !== minRange);

    const isShowVariants = Boolean(
        options && 0 < options.length && 0 < options.filter((option) => 0 < option.count).length,
    );

    if (!isShowInputs && !isShowVariants) {
        return null;
    }

    return (
        <div className={style.main}>
            {isShowInputs && (
                <div className={style.rangeContainer}>
                    <FilterInput value={minValue} onChange={handleChangeMin} placeholder={minPlaceholder} />
                    {/* <span className={style.separator}>–</span>*/}
                    <FilterInput value={maxValue} onChange={handleChangeMax} placeholder={maxPlaceholder} />
                </div>
            )}
            {isShowVariants && (
                <div>
                    {options
                        ?.filter((option) => 0 < option.count)
                        .map((option) => {
                            const {code, count, max, min, title} = option;
                            const isMinEqual = min === Number(minValue);
                            const isMaxEqual = max === Number(maxValue);
                            return (
                                <Radio
                                    count={withCount ? count : ''}
                                    isChecked={isMinEqual && isMaxEqual}
                                    key={`${code}${title}`}
                                    name={code}
                                    onChange={() => handleSelectOption(min, max)}
                                    title={title}
                                />
                            );
                        })}
                </div>
            )}
        </div>
    );
};
